import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { authorizedFetch } from "../auth/authorizedFetch";
import { throwIfNotOk } from "./errorEnvelope";
import type { PlatformName } from "./fga-types";
import type {
  ClientListItem,
  CreatePlatformClientSpec,
  CreatePlatformRequest,
  PlatformDetail,
  PlatformListItem,
  UpdatePlatformRequest,
} from "./types";

const API_BASE = import.meta.env.VITE_KEYCLOAK_API_URL ?? "/keycloak-api";

export function usePlatforms() {
  return useQuery({
    queryKey: ["platforms"],
    queryFn: async () => {
      const res = await authorizedFetch(`${API_BASE}/platforms`);
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<PlatformListItem[]>;
    },
    staleTime: 1000 * 60,
  });
}

// The display names the blueprint canvas shows for Platform slugs, from the
// caller's own Platforms. A blueprint has no display name of its own; the
// Platform is the source (ADR-0059 decision 4). A slug with no entry here is
// shown as the slug.
export function usePlatformNames(): PlatformName[] {
  const { data: platforms = [] } = usePlatforms();
  return useMemo(
    () =>
      platforms.flatMap((p) =>
        p.slug ? [{ slug: p.slug, displayName: p.name }] : [],
      ),
    [platforms],
  );
}

export function usePlatform(id: string) {
  return useQuery({
    queryKey: ["platforms", id],
    enabled: !!id,
    queryFn: async () => {
      const res = await authorizedFetch(`${API_BASE}/platforms/${id}`);
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<PlatformDetail>;
    },
  });
}

export function useCreatePlatform() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreatePlatformRequest) => {
      const res = await authorizedFetch(`${API_BASE}/platforms`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
      });
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<PlatformDetail>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platforms"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
  });
}

export function useUpdatePlatform(platformId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: UpdatePlatformRequest) => {
      const res = await authorizedFetch(`${API_BASE}/platforms/${platformId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
      });
      await throwIfNotOk(res, "kc");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platforms", platformId] });
      queryClient.invalidateQueries({ queryKey: ["platforms"] });
    },
  });
}

export function useDeletePlatform() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (platformId: string) => {
      const res = await authorizedFetch(`${API_BASE}/platforms/${platformId}`, {
        method: "DELETE",
      });
      await throwIfNotOk(res, "kc");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platforms"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
  });
}

export function useRetryPlatformProvisioning(platformId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await authorizedFetch(
        `${API_BASE}/platforms/${platformId}/retry`,
        { method: "POST" },
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<PlatformDetail>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platforms", platformId] });
      queryClient.invalidateQueries({ queryKey: ["platforms"] });
    },
  });
}

export function useAddClientToPlatform(platformId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (spec: CreatePlatformClientSpec) => {
      const res = await authorizedFetch(
        `${API_BASE}/platforms/${platformId}/clients`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(spec),
        },
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<ClientListItem>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platforms", platformId] });
      queryClient.invalidateQueries({ queryKey: ["platforms"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
  });
}

// The platform onboarding checklist. Dormant (ADR-0060 decision 8): the
// "authentication" key is retired with the Login-wired-up step, so nothing
// reads it. The endpoints stay as the home of the future "Pushed to prod" key.
export function useOnboardingProgress(platformId: string) {
  return useQuery({
    queryKey: ["platforms", platformId, "onboarding"],
    enabled: !!platformId,
    queryFn: async () => {
      const res = await authorizedFetch(
        `${API_BASE}/platforms/${platformId}/onboarding`,
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<{ completed: string[] }>;
    },
  });
}

export function useUpdateOnboardingProgress(platformId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (completed: string[]) => {
      const res = await authorizedFetch(
        `${API_BASE}/platforms/${platformId}/onboarding`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ completed }),
        },
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<{ completed: string[] }>;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["platforms", platformId, "onboarding"], data);
    },
  });
}
